// components/ModalUsuariosReadOnly.js
import React, { useState, useMemo, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Box,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material"
import { Search, Close, ArrowBack } from "@mui/icons-material"

const ModalUsuariosReadable = ({ open, onClose, usuarios, eventos, loading }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null)
  const [busquedaTareas, setBusquedaTareas] = useState("")

  // Filtrar usuarios por nombre o código
  const usuariosFiltrados = useMemo(
    () =>
      usuarios.filter(
        (usuario) =>
          usuario.usrnombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          usuario.usrcodigo.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [usuarios, searchTerm],
  )

  // Obtener tareas por usuario
  const getTareasPorUsuario = useMemo(() => {
    const tareasPorUsuario = {}
    eventos.forEach((evento) => {
      const usrcodigo = evento.extendedProps.usrcodigo
      if (!tareasPorUsuario[usrcodigo]) {
        tareasPorUsuario[usrcodigo] = []
      }
      tareasPorUsuario[usrcodigo].push(evento)
    })
    return tareasPorUsuario
  }, [eventos])

  // Filtrar tareas del usuario seleccionado
  const tareasFiltradas = useMemo(() => {
    if (!usuarioSeleccionado) return []

    const tareasUsuario = getTareasPorUsuario[usuarioSeleccionado.usrcodigo] || []

    if (!busquedaTareas) return tareasUsuario

    return tareasUsuario.filter(
      (tarea) =>
        tarea.extendedProps.locdescri?.toLowerCase().includes(busquedaTareas.toLowerCase()) ||
        tarea.extendedProps.tareaDescripcion?.toLowerCase().includes(busquedaTareas.toLowerCase()) ||
        new Date(tarea.start).toLocaleDateString().includes(busquedaTareas) ||
        tarea.title.toLowerCase().includes(busquedaTareas.toLowerCase()),
    )
  }, [usuarioSeleccionado, getTareasPorUsuario, busquedaTareas])

  const handleUsuarioClick = (usuario) => {
    setUsuarioSeleccionado(usuario)
    setBusquedaTareas("")
  }

  const handleBackToList = () => {
    setUsuarioSeleccionado(null)
    setBusquedaTareas("")
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getHorarioTarea = (tarea) => {
    const startTime = formatTime(tarea.start)
    const endTime = formatTime(tarea.end)
    return `${startTime} - ${endTime}`
  }

  // Obtener color según el estado de la tarea
  const getEstadoColor = (tarea) => {
    const estado = tarea.extendedProps.estado
    switch (estado) {
      case "completado":
        return "success"
      case "en-progreso":
        return "warning"
      case "pendiente":
        return "default"
      case "cancelado":
        return "error"
      default:
        return "default"
    }
  }

  // Obtener texto del estado
  const getEstadoText = (tarea) => {
    const estado = tarea.extendedProps.estado
    switch (estado) {
      case "completado":
        return "Completado"
      case "en-progreso":
        return "En Progreso"
      case "pendiente":
        return "Pendiente"
      case "cancelado":
        return "Cancelado"
      default:
        return "Sin estado"
    }
  }

  useEffect(() => {
    if (!open) {
      // Se cerró el modal, reseteamos todo
      setUsuarioSeleccionado(null)
      setBusquedaTareas("")
      setSearchTerm("")
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {usuarioSeleccionado ? (
            <>
              <IconButton onClick={handleBackToList} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h6">{usuarioSeleccionado.usrnombre}</Typography>
              <div style={{ width: 48 }} />
            </>
          ) : (
            <>
              <Typography variant="h6">Lista de Usuarios (Solo Lectura)</Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {!usuarioSeleccionado ? (
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar usuario por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{ mb: 2 }}
            />

            <List>
              {usuariosFiltrados.map((usuario) => (
                <React.Fragment key={usuario.usrcodigo}>
                  <ListItem
                    button
                    onClick={() => handleUsuarioClick(usuario)}
                    sx={{
                      "&:hover": { backgroundColor: "action.hover" },
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">{usuario.usrnombre}</Typography>
                          <Chip
                            label={`Código: ${usuario.usrcodigo}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          {getTareasPorUsuario[usuario.usrcodigo]?.length || 0} tareas programadas
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip label={getTareasPorUsuario[usuario.usrcodigo]?.length || 0} color="primary" size="small" />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>

            {usuariosFiltrados.length === 0 && (
              <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
                No se encontraron usuarios
              </Typography>
            )}
          </>
        ) : (
          <Box>
            {/* Barra de búsqueda de tareas (solo lectura también disponible) */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar tareas por localidad, descripción o fecha..."
              value={busquedaTareas}
              onChange={(e) => setBusquedaTareas(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{ mb: 2 }}
            />

            {loading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Total de tareas: {tareasFiltradas.length}
              </Typography>

              {tareasFiltradas.length > 0 ? (
                <List>
                  {tareasFiltradas.map((tarea, index) => (
                    <React.Fragment key={tarea.id}>
                      <ListItem
                        sx={{
                          flexDirection: "column",
                          alignItems: "flex-start",
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box width="100%">
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {formatDate(tarea.start)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {tarea.extendedProps.locdescri || "Sin localidad"}
                              </Typography>
                            </Box>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                              <Chip
                                label={getEstadoText(tarea)}
                                size="small"
                                color={getEstadoColor(tarea)}
                                variant="outlined"
                              />
                              <Chip label={getHorarioTarea(tarea)} size="small" variant="outlined" color="primary" />
                            </Box>
                          </Box>

                          <Box mb={1}>
                            <Typography variant="body1" fontWeight="medium" gutterBottom>
                              {tarea.title}
                            </Typography>

                            {tarea.extendedProps.tareaDescripcion && (
                              <Typography variant="body2" color="textSecondary">
                                {tarea.extendedProps.tareaDescripcion}
                              </Typography>
                            )}
                          </Box>

                          {/* Información adicional de solo lectura */}
                          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                            {tarea.extendedProps.direccion && (
                              <Chip label={`📍 ${tarea.extendedProps.direccion}`} size="small" variant="outlined" />
                            )}

                            {tarea.extendedProps.prioridad && (
                              <Chip
                                label={`Prioridad: ${tarea.extendedProps.prioridad}`}
                                size="small"
                                color={
                                  tarea.extendedProps.prioridad === "alta"
                                    ? "error"
                                    : tarea.extendedProps.prioridad === "media"
                                      ? "warning"
                                      : "default"
                                }
                                variant="outlined"
                              />
                            )}

                            {tarea.extendedProps.duracionEstimada && (
                              <Chip
                                label={`⏱️ ${tarea.extendedProps.duracionEstimada}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      {index < tareasFiltradas.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
                  {busquedaTareas
                    ? "No se encontraron tareas que coincidan con la búsqueda"
                    : "No hay tareas programadas para este usuario"}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={usuarioSeleccionado ? handleBackToList : onClose}>
          {usuarioSeleccionado ? "Volver a la lista" : "Cerrar"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalUsuariosReadable
